package com.arena_web.ui.components.dropdown

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.SportDto
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.SportColorScheme
import com.arena_web.ui.theme.SportColors
import com.arena_web.ui.utils.m
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.mFontSize
import com.arena_web.ui.utils.mGap
import com.arena_web.ui.utils.mLetterSpacing
import com.arena_web.ui.utils.mLineHeight
import com.arena_web.ui.utils.mPadding
import com.arena_web.ui.utils.mPaddingLeft
import com.arena_web.ui.utils.mPaddingRight
import com.arena_web.ui.utils.mSize
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

/** Canonical display order; unlisted sports follow, "Other" is always last. */
private val SPORT_DISPLAY_ORDER = listOf("football", "padel", "tennis", "basketball", "volleyball")

/**
 * Shared sport-picker dropdown panel — Figma 10702:203681.
 *
 * One source of truth for both the mobile top bar and the desktop AppHeader.
 * Sizes use the project's `m()` helpers which clamp at 1 px above the 390-px
 * mobile baseline, so the panel renders at design size on desktop and scales
 * down on narrower phones.
 *
 * Caller is responsible for positioning (e.g. wrapping in a `position: absolute`
 * container or a Modifier with absolute anchors).
 *
 * The list is automatically prefixed with a synthetic "All" pseudo-sport and
 * sorted in the canonical display order (Football, Padel, Tennis, Basketball,
 * Volleyball, then unlisted sports, "Other" last).
 */
@Composable
fun SportDropdownPanel(
    selectedSportName: String,
    sportsList: List<SportDto>,
    onSelect: (SportDto) -> Unit,
    modifier: Modifier = Modifier,
    // When true, prepends a synthetic "All" pseudo-sport (id="") at the top.
    // App-level filters want this. Forms that require a concrete sport (e.g.
    // CreateEventPopup) pass `false`.
    prefixAll: Boolean = true,
    // Multiplier applied to every internal `m()` value. The helpers expand to
    // `calc(N * var(--mobile-scale))`, so overriding `--mobile-scale` on the
    // panel root scales all sizes (chip, text, padding, gap, min-width) in
    // one go. 1.0 = design size; 0.75 = compact for embedded form fields.
    scale: Double = 1.0,
) {
    Box(
        modifier = Modifier
            .mBorderRadius(26)
            .mPadding(v = 12, h = 0)
            .styleModifier {
                property("background", "#09142d")
                property("min-width", m(200))
                if (scale != 1.0) property("--mobile-scale", "${scale}px")
            }
            .then(modifier)
    ) {
        Column(modifier = Modifier.fillMaxWidth().mGap(8)) {
            val allOption = SportDto(
                id = "",
                name = "All",
                icon = "",
                maxPlayersPerTeam = 0,
                minPlayersPerTeam = 0,
                createdAt = "",
                updatedAt = "",
            )
            val orderedSports = (if (prefixAll) listOf(allOption) else emptyList()) +
                sportsList.sortedBy { sport ->
                    val index = SPORT_DISPLAY_ORDER.indexOf(sport.name.lowercase())
                    when {
                        index >= 0 -> index
                        sport.name.equals("Other", ignoreCase = true) -> SPORT_DISPLAY_ORDER.size + 1
                        else -> SPORT_DISPLAY_ORDER.size
                    }
                }
            orderedSports.forEach { sport ->
                SportDropdownPanelRow(
                    sport = sport,
                    isSelected = sport.name.equals(selectedSportName, ignoreCase = true),
                    onSelect = { onSelect(sport) },
                )
            }
        }
    }
}

@Composable
private fun SportDropdownPanelRow(
    sport: SportDto,
    isSelected: Boolean,
    onSelect: () -> Unit,
) {
    val colors = SportColors.forSport(sport.name.lowercase())
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .mPaddingLeft(12)
            .mPaddingRight(16)
            .mGap(8)
            .cursor(Cursor.Pointer)
            .onClick { onSelect() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SportDropdownPanelIcon(sport.name, colors)
        SpanText(
            sport.name.replaceFirstChar { it.uppercase() },
            modifier = Modifier
                .flexGrow(1)
                .mFontSize(16)
                .mLineHeight(24)
                .mLetterSpacing(0.16)
                .fontWeight(400)
                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                .color(if (isSelected) AppColors.PrimaryWhite else AppColors.Text.Secondary)
        )
        if (isSelected) {
            // Mask-image instead of <img> so the SVG can be recoloured to the
            // brand blue (`AppIcons.Check` ships with a default light-blue fill
            // and the Icon composable uses <img> which doesn't honour outer
            // CSS variables).
            Box(
                modifier = Modifier
                    .mSize(20)
                    .styleModifier {
                        property("background-color", AppColors.Primary.toString())
                        property("-webkit-mask-image", "url(\"${AppIcons.Check}\")")
                        property("mask-image", "url(\"${AppIcons.Check}\")")
                        property("-webkit-mask-size", "contain")
                        property("mask-size", "contain")
                        property("-webkit-mask-repeat", "no-repeat")
                        property("mask-repeat", "no-repeat")
                        property("-webkit-mask-position", "center")
                        property("mask-position", "center")
                    }
            )
        }
    }
}

@Composable
private fun SportDropdownPanelIcon(
    sportName: String,
    colors: SportColorScheme,
) {
    Box(
        modifier = Modifier
            .mSize(32)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        // Figma 10702:203681 ships its own round-ball glyph set
        // (`SportFootball`/`SportBasketball`/etc.) which is separate from the
        // line-art `Basketball`/`Tennis`/… family used elsewhere in the app.
        // "All" → globe (every sport); "Other" → round "more" badge.
        val iconSrc = when (sportName.lowercase()) {
            "all" -> AppIcons.Globe
            "football" -> AppIcons.SportFootball
            "basketball" -> AppIcons.SportBasketball
            "volleyball" -> AppIcons.SportVolleyball
            "tennis" -> AppIcons.SportTennis
            "padel" -> AppIcons.SportPadel
            "other", "more" -> AppIcons.SportOther
            else -> AppIcons.SportOther
        }
        // Each glyph is tinted with its own sport's accent colour (multicolour
        // list) — "All"/"Other" fall back to the Football scheme's blue.
        val iconColor = colors.primary.toString()
        // All glyphs (incl. the round "All" globe and "Other" badge) render at
        // the same size so none look undersized next to the ball icons.
        val iconSize = 18
        Box(
            modifier = Modifier
                .mSize(iconSize)
                .styleModifier {
                    property("background-color", iconColor)
                    property("-webkit-mask-image", "url(\"$iconSrc\")")
                    property("mask-image", "url(\"$iconSrc\")")
                    property("-webkit-mask-size", "contain")
                    property("mask-size", "contain")
                    property("-webkit-mask-repeat", "no-repeat")
                    property("mask-repeat", "no-repeat")
                    property("-webkit-mask-position", "center")
                    property("mask-position", "center")
                }
        )
    }
}
